import apiRoutes from 'config/apiRoute';
import apiRequests from 'utils/api';
import { AxiosResponse } from 'utils/interface';
import { localRandomUuid } from 'utils/string.utils';
import { toEntry, toTimer, toTimerList } from './mappers';
import {
  CompleteTimerInput,
  CreateEntryInput,
  OpenTimersParams,
  StartTimerInput,
  Timer,
  TimeEntry,
  UpdateEntryInput,
} from './types';

const { TIME_ENTRIES, TIMERS, TIMERS_CURRENT, TIMERS_START } = apiRoutes;

const withRefToken = <T extends Record<string, any>>(body: T) => ({
  ...body,
  ref_token: localRandomUuid,
});

export const startTimer = ({ source, sourceUuid }: StartTimerInput): Promise<Timer> =>
  apiRequests.post(TIMERS_START, withRefToken({ source, source_uuid: sourceUuid })).then(toTimer);

export const pauseTimer = (uuid: string): Promise<Timer> =>
  apiRequests.post(`${TIMERS}/${uuid}/pause`, withRefToken({})).then(toTimer);

export const resumeTimer = (uuid: string): Promise<Timer> =>
  apiRequests.post(`${TIMERS}/${uuid}/resume`, withRefToken({})).then(toTimer);

export const completeTimer = (uuid: string, input: CompleteTimerInput = {}): Promise<Timer> =>
  apiRequests.post(`${TIMERS}/${uuid}/complete`, withRefToken(input)).then(toTimer);

export const fetchCurrentTimers = (abort?: AbortController): Promise<Timer[]> =>
  apiRequests.get(TIMERS_CURRENT, {}, {}, abort).then(toTimerList);

export const fetchOpenTimers = (params: OpenTimersParams = {}): Promise<Timer[]> => {
  const query: Record<string, any> = { 'page[size]': params.pageSize ?? 999 };
  if (params.source) query['filter[source]'] = params.source;
  if (params.status) query['filter[status]'] = params.status;
  if (params.user) query['filter[user]'] = params.user;

  return apiRequests.get(TIMERS, query).then(toTimerList);
};

export const createEntry = (input: CreateEntryInput): Promise<TimeEntry> => {
  const body = withRefToken({
    source: input.source,
    source_uuid: input.sourceUuid,
    user_uuid: input.userUuid,
    date: input.date,
    minutes: input.minutes,
    description: input.description ?? '',
    ...(input.billable !== undefined ? { billable: input.billable } : {}),
  });

  return apiRequests.post(TIME_ENTRIES, body).then(toEntry);
};

export const updateEntry = (uuid: string, input: UpdateEntryInput): Promise<TimeEntry> => {
  const body: Record<string, any> = {};
  if (input.userUuid !== undefined) body.user_uuid = input.userUuid;
  if (input.date !== undefined) body.date = input.date;
  if (input.minutes !== undefined) body.minutes = input.minutes;
  if (input.description !== undefined) body.description = input.description;
  if (input.billable !== undefined) body.billable = input.billable;

  return apiRequests.put(`${TIME_ENTRIES}/${uuid}`, withRefToken(body)).then(toEntry);
};

export const deleteEntry = (uuid: string): Promise<AxiosResponse> =>
  apiRequests.delete(`${TIME_ENTRIES}/${uuid}`);

export const createManualPausedEntry = async (input: CreateEntryInput): Promise<Timer> => {
  const started = await startTimer({ source: input.source, sourceUuid: input.sourceUuid });
  const paused = await pauseTimer(started.uuid);

  if (input.minutes && input.minutes > 0) {
    await updateEntry(paused.uuid, {
      date: input.date,
      minutes: input.minutes,
      description: input.description,
    });
  }

  return {
    ...paused,
    date: input.date,
    minutes: input.minutes ?? paused.minutes,
    comment: input.description ?? paused.comment,
  };
};

const timeTrackingService = {
  startTimer,
  pauseTimer,
  resumeTimer,
  completeTimer,
  fetchCurrentTimers,
  fetchOpenTimers,
  createEntry,
  updateEntry,
  deleteEntry,
  createManualPausedEntry,
};

export default timeTrackingService;
